/**
 * MindPillarInventory — vibe-designer 1.190.0 Phase 7.6.
 *
 * Surfaces the per-pillar chunk count for MIND on the Knowledge tab
 * Overview. Helps users see when chip-filter is useful (e.g., "voice has
 * 23 docs, click the chip to focus on them") vs when MIND has no
 * brand-system tagged content yet (e.g., NULL-only inventory).
 *
 * NULL pillar entries are surfaced explicitly as "(unpilled)" — they're
 * bulk-ingested platform-ops docs per v9 §5.3 reframe; users should NOT
 * confuse them with missing tags.
 *
 * Sourced from GET /api/knowledge/services/mind/pillars (Phase 7.6 route).
 * Honest-empty when pgvector unconfigured or MIND has zero rows.
 */
import { useEffect } from 'react';
import { Database, Loader2 } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';

export function MindPillarInventory() {
  const breakdown = useKnowledgeStore((s) => s.mindPillarBreakdown);
  const isFetching = useKnowledgeStore((s) => s.isFetchingMindPillarBreakdown);
  const error = useKnowledgeStore((s) => s.mindPillarBreakdownError);
  const fetchBreakdown = useKnowledgeStore((s) => s.fetchMindPillarBreakdown);
  // 1.191.0 Phase 7.6.1 — re-fetch on SSE ingest event so pillar counts stay live.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);

  useEffect(() => {
    void fetchBreakdown();
  }, [fetchBreakdown, lastIngestAt]);

  const total = (breakdown?.pillars ?? []).reduce((s, e) => s + e.count, 0);

  return (
    <section
      data-testid="mind-pillar-inventory"
      className="border-t border-vibe-border pt-3 mt-3"
    >
      <header className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          <Database className="w-3 h-3 text-cyan-400" />
          THE MIND — Pillar inventory
        </span>
        {breakdown && breakdown.available && total > 0 && (
          <span className="text-vibe-text-secondary normal-case font-normal">
            {total} doc{total === 1 ? '' : 's'} total
          </span>
        )}
      </header>

      {isFetching && (
        <div
          data-testid="mind-pillar-inventory-loading"
          className="flex items-center gap-2 text-sm text-vibe-text-secondary py-2"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading pillar inventory…</span>
        </div>
      )}

      {!isFetching && error && (
        <div
          data-testid="mind-pillar-inventory-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-sm text-vibe-error"
        >
          Network error: {error.message}
        </div>
      )}

      {/* R2 Concern #2 fix — render "unavailable" branch when EITHER
          available=false OR (available=true AND error is set). The
          mid-flight-query-failure path returns available=true + error
          (mirrors stats_extended contract); without this clause, the
          empty-state branch would silently mask the error string. */}
      {!isFetching && !error && breakdown && (!breakdown.available || breakdown.error) && (
        <div
          data-testid="mind-pillar-inventory-unavailable"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {breakdown.error ?? 'MIND pillar inventory unavailable.'}
        </div>
      )}

      {!isFetching && !error && breakdown && breakdown.available && !breakdown.error && breakdown.pillars.length === 0 && (
        <div
          data-testid="mind-pillar-inventory-empty"
          className="text-sm text-vibe-text-secondary py-2"
        >
          No documents ingested yet — use the ingest panel above to add brand-system docs.
        </div>
      )}

      {!isFetching && !error && breakdown && breakdown.available && !breakdown.error && breakdown.pillars.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {breakdown.pillars.map((entry) => {
            const isNull = entry.pillar === null;
            const label = isNull ? '(unpilled)' : entry.pillar;
            // R2 Concern #3 fix — separate keys + testids per type. A
            // tenant could theoretically create a pillar literally named
            // 'null-pillar' or any string; with the previous `${pillar ?? '__null__'}`
            // pattern a real pillar named '__null__' would collide with
            // the NULL sentinel. Now: null branch uses a stable sentinel
            // testid; non-null uses the pillar value directly. The 'null'
            // key prefix prevents string collision since pillar='null'
            // would be a distinct row (still keyed by `pillar:null` etc.).
            const itemKey = isNull ? 'null-pillar' : `pillar:${entry.pillar}`;
            const testId = isNull
              ? 'mind-pillar-inventory-entry-null-pillar'
              : `mind-pillar-inventory-entry-${entry.pillar}`;
            return (
              <li
                key={itemKey}
                data-testid={testId}
                className={
                  isNull
                    ? 'inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded border border-vibe-border bg-vibe-bg text-vibe-text-secondary'
                    : 'inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded border border-cyan-700/40 bg-cyan-900/20 text-cyan-200'
                }
                title={
                  isNull
                    ? 'Platform-ops docs — no brand pillar. See PillarChips banner for details.'
                    : `Brand-system pillar "${entry.pillar}" with ${entry.count} doc${entry.count === 1 ? '' : 's'}`
                }
              >
                <span className="font-medium">{label}</span>
                <span className="tabular-nums text-vibe-text-secondary">{entry.count}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
